import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import {
    addCollateralDocument,
    createCollateral,
    deleteCollateral,
    deleteCollateralDocument,
    getCollateralDetail,
    linkCollateralToLoan,
    listLoanCollateralLinks,
    searchCollaterals,
    unlinkCollateralFromLoan,
    updateCollateral,
} from '../api/collateralApi';
import {
    Collateral,
    CollateralDocument,
    CollateralSearchParams,
    LinkCollateralRequest,
    LoanKey,
} from '../types/collateral.types';

// ─── Query keys ─────────────────────────────────────────────────────────────

const keys = {
    all: ['collaterals'] as const,
    search: (params: CollateralSearchParams) =>
        [...keys.all, 'search', params] as const,
    detail: (id: number) => [...keys.all, 'detail', id] as const,
    loanLinks: (k: LoanKey) =>
        [...keys.all, 'loan-links', k.branch, k.glNo, k.acNo, k.cycle] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

/** Paged list of collaterals (defaults to current user's branch on the server). */
export const useCollateralSearch = (params: CollateralSearchParams = {}) =>
    useQuery({
        queryKey: keys.search(params),
        queryFn: () => searchCollaterals(params),
    });

/** Fetch one collateral with its documents and loan links. */
export const useCollateralDetail = (id: number | null | undefined) =>
    useQuery({
        queryKey: id ? keys.detail(id) : [...keys.all, 'detail', 'disabled'],
        queryFn: () => getCollateralDetail(id as number),
        enabled: id != null && id > 0,
    });

/** List every collateral link (active + released) for a specific loan. */
export const useLoanCollateralLinks = (k: LoanKey | null) =>
    useQuery({
        queryKey: k ? keys.loanLinks(k) : [...keys.all, 'loan-links', 'disabled'],
        queryFn: () =>
            listLoanCollateralLinks(k!.branch, k!.glNo, k!.acNo, k!.cycle),
        enabled: Boolean(k?.branch && k?.glNo && k?.acNo && k?.cycle != null),
    });

// ─── Collateral mutations ───────────────────────────────────────────────────

export const useCollateralMutations = () => {
    const qc = useQueryClient();

    const invalidateLists = () => {
        qc.invalidateQueries({ queryKey: keys.all });
    };

    const create = useMutation({
        mutationFn: (body: Collateral) => createCollateral(body),
        onSuccess: () => {
            message.success('Collateral registered');
            invalidateLists();
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.message || 'Failed to register collateral');
        },
    });

    const update = useMutation({
        mutationFn: ({ id, body }: { id: number; body: Collateral }) =>
            updateCollateral(id, body),
        onSuccess: (_data, { id }) => {
            message.success('Collateral updated');
            qc.invalidateQueries({ queryKey: keys.detail(id) });
            invalidateLists();
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.message || 'Failed to update collateral');
        },
    });

    const remove = useMutation({
        mutationFn: (id: number) => deleteCollateral(id),
        onSuccess: () => {
            message.success('Collateral deleted');
            invalidateLists();
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.message || 'Failed to delete collateral');
        },
    });

    const addDocument = useMutation({
        mutationFn: ({
            collateralId,
            doc,
        }: {
            collateralId: number;
            doc: CollateralDocument;
        }) => addCollateralDocument(collateralId, doc),
        onSuccess: (_data, { collateralId }) => {
            message.success('Document attached');
            qc.invalidateQueries({ queryKey: keys.detail(collateralId) });
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.message || 'Failed to attach document');
        },
    });

    const removeDocument = useMutation({
        mutationFn: ({
            collateralId,
            documentId,
        }: {
            collateralId: number;
            documentId: number;
        }) => deleteCollateralDocument(collateralId, documentId),
        onSuccess: (_data, { collateralId }) => {
            message.success('Document removed');
            qc.invalidateQueries({ queryKey: keys.detail(collateralId) });
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.message || 'Failed to remove document');
        },
    });

    return { create, update, remove, addDocument, removeDocument };
};

// ─── Link mutations (per loan) ──────────────────────────────────────────────

export const useLoanCollateralLinkMutations = (k: LoanKey | null) => {
    const qc = useQueryClient();

    const invalidate = () => {
        if (k) qc.invalidateQueries({ queryKey: keys.loanLinks(k) });
        qc.invalidateQueries({ queryKey: keys.all });
    };

    const link = useMutation({
        mutationFn: (body: LinkCollateralRequest) =>
            linkCollateralToLoan(k!.branch, k!.glNo, k!.acNo, k!.cycle, body),
        onSuccess: () => {
            message.success('Collateral pledged to loan');
            invalidate();
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.message || 'Failed to pledge collateral');
        },
    });

    const unlink = useMutation({
        mutationFn: (linkId: number) =>
            unlinkCollateralFromLoan(k!.branch, k!.glNo, k!.acNo, k!.cycle, linkId),
        onSuccess: () => {
            message.success('Collateral released from loan');
            invalidate();
        },
        onError: (err: any) => {
            message.error(err?.response?.data?.message || 'Failed to release collateral');
        },
    });

    return { link, unlink };
};
